import { datadogLogs } from '@datadog/browser-logs'

import { AppState } from './authContext'

export type AuthOperationKind =
  | 'login'
  | 'reset'
  | 'reset-logout'
  | 'logout'
  | 'token'

interface AuthOperation {
  id: string
  kind: AuthOperationKind
  createdAt: number
  appState: AppState
  resumePath?: string
}

const key = 'pendingAuthOperation'
const maxAge = 30 * 60 * 1000
let sequence = 0

export const safeReturnTo = (value?: string): string => {
  if (
    typeof value !== 'string' ||
    !value.startsWith('/') ||
    value.startsWith('//') ||
    value.includes('\\')
  ) {
    return '/auth0'
  }
  const url = new URL(value, window.location.origin)
  return url.origin !== window.location.origin ||
    url.pathname.replace(/\/+$/, '') === '/password-reset'
    ? '/auth0'
    : value
}

export const clearAuthOperation = (expectedId?: string): void => {
  if (expectedId && getAuthOperation()?.id !== expectedId) return
  sessionStorage.removeItem(key)
}

export const getAuthOperation = (): AuthOperation | undefined => {
  const stored = sessionStorage.getItem(key)
  if (!stored) return undefined
  try {
    const operation: AuthOperation = JSON.parse(stored)
    if (
      operation &&
      typeof operation.id === 'string' &&
      ['login', 'reset', 'reset-logout', 'logout', 'token'].includes(
        operation.kind,
      ) &&
      typeof operation.createdAt === 'number' &&
      operation.createdAt <= Date.now() &&
      Date.now() - operation.createdAt < maxAge &&
      operation.appState &&
      typeof operation.appState === 'object' &&
      !Array.isArray(operation.appState) &&
      Object.entries(operation.appState).every(([key, value]) =>
        key === 'driverCase'
          ? typeof value === 'boolean'
          : typeof value === 'string',
      )
    ) {
      return operation
    }
  } catch (error: unknown) {
    datadogLogs.logger.error('Invalid stored authentication operation', {
      error,
    })
  }
  clearAuthOperation()
  return undefined
}

export const beginAuthOperation = (
  kind: AuthOperationKind,
  appState: AppState,
  resumePath?: string,
): string => {
  const id = `${Date.now()}:${++sequence}`
  sessionStorage.setItem(
    key,
    JSON.stringify({
      id,
      kind,
      appState,
      resumePath,
      createdAt: Date.now(),
    }),
  )
  return id
}

// This is a navigation hint only. MSAL validates OAuth state, nonce and tokens.
export const isAuthCallback = (search: string, hash: string): boolean =>
  [search, hash].some((part) => {
    const params = new URLSearchParams(part.replace(/^[?#]/, ''))
    return (
      params.has('state') ||
      params.has('code') ||
      params.has('id_token') ||
      params.has('error')
    )
  })

export const getOperationForLocation = (
  pathname: string,
  search: string,
  hash: string,
): AuthOperation | undefined => {
  const operation = getAuthOperation()
  if (!operation) return undefined
  if (
    isAuthCallback(search, hash) ||
    operation.resumePath === pathname + search
  ) {
    return operation
  }
  clearAuthOperation()
  return undefined
}
